import { useParams, Link, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { 
  PencilIcon,
  TrashIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PlusIcon,
  TagIcon,
  EyeIcon,
  CurrencyEuroIcon,
  CubeIcon
} from '@heroicons/react/24/outline'
import { GET_OPTION_GROUP, GET_OPTION_ITEMS_BY_GROUP, GET_VARIANT_OPTION_GROUPS_BY_GROUP } from '../graphql/product-queries'
import { DELETE_OPTION_GROUP } from '../graphql/mutations'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

const OptionGroupDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  
  const handleBackNavigation = () => {
    // Check if we came from a variant detail page
    const state = location.state as { from?: string; variantId?: string; productId?: number } | null
    if (state?.from === 'variant-detail' && state.variantId && state.productId) {
      navigate(`/products/${state.productId}/variants/${state.variantId}`)
    } else {
      // Default back to option groups list
      navigate('/option-groups')
    }
  }
  
  const { data, loading, error } = useQuery(GET_OPTION_GROUP, {
    variables: { id: parseInt(id!) },
    skip: !id
  })

  const { data: optionItemsData, loading: itemsLoading } = useQuery(GET_OPTION_ITEMS_BY_GROUP, {
    variables: { optionGroupId: parseInt(id!) },
    skip: !id
  })

  const { data: variantsData, loading: variantsLoading } = useQuery(GET_VARIANT_OPTION_GROUPS_BY_GROUP, {
    variables: { optionGroupId: parseInt(id!) },
    skip: !id
  })

  const [deleteOptionGroup] = useMutation(DELETE_OPTION_GROUP, {
    onCompleted: () => {
      toast.success('Option group deleted successfully')
      navigate('/option-groups')
    },
    onError: (error) => {
      toast.error(`Failed to delete option group: ${error.message}`)
    }
  })



  const handleDelete = async () => {
    if (data?.optionGroup && window.confirm(`Are you sure you want to delete "${data.optionGroup.name}"? This action cannot be undone.`)) {
      try {
        await deleteOptionGroup({
          variables: { id: Number(data.optionGroup.id) }
        })
      } catch (error) {
        // Error is handled in the mutation's onError callback
      }
    }
  }



  const handleOptionItemClick = (itemId: number) => {
    navigate(`/option-items/${itemId}`)
  }

  const handleVariantClick = (productId: number, variantId: number) => {
    navigate(`/products/${productId}/variants/${variantId}`)
  }

  const formatPrice = (cents: number) => {
    if (cents === 0) return 'No charge'
    const sign = cents > 0 ? '+' : ''
    return `${sign}€${(cents / 100).toFixed(2)}`
  }

  if (loading || itemsLoading || variantsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading option group</h3>
        <p className="text-gray-500 mb-6">{error.message}</p>
        <button onClick={handleBackNavigation} className="btn-primary">
          Back to Option Groups
        </button>
      </div>
    )
  }

  if (!data?.optionGroup) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Option group not found</h3>
        <p className="text-gray-500 mb-6">The option group you're looking for doesn't exist.</p>
        <button onClick={handleBackNavigation} className="btn-primary">
          Back to Option Groups
        </button>
      </div>
    )
  }

  const optionGroup = data.optionGroup
  const optionItems = optionItemsData?.optionItemsByGroup || []
  const connectedVariants = variantsData?.variantOptionGroupsByGroup || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackNavigation}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{optionGroup.name}</h1>
            <p className="mt-1 text-sm text-gray-500">Option Group Details</p>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <Link
            to={`/option-groups/${optionGroup.id}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </Link>
          <button
            onClick={handleDelete}
            className="btn-danger"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Option Group Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Details */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Basic Information</h2>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center space-x-3">
                <CogIcon className="h-8 w-8 text-gray-400" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{optionGroup.name}</h3>
                  <p className="text-sm text-gray-500">ID: {optionGroup.id}</p>
                </div>
              </div>

              {optionGroup.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <p className="text-sm text-gray-600">{optionGroup.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Selections
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                      {optionGroup.min_select}
                    </span>
                    <span className="text-sm text-gray-500">
                      {optionGroup.min_select === 0 ? 'Optional' : 'Required'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Selections
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      {optionGroup.max_select}
                    </span>
                    <span className="text-sm text-gray-500">
                      {optionGroup.max_select === 1 ? 'Single choice' : 'Multiple choice'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ask During Add to Cart
                </label>
                <div className="flex items-center space-x-2">
                  {optionGroup.ask_during_add ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      <XCircleIcon className="h-4 w-4 mr-1" />
                      No
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    {optionGroup.ask_during_add 
                      ? 'Shown when adding products to cart'
                      : 'Not shown during add to cart'
                    }
                  </span>
                </div>
              </div>

              {optionGroup.sort_order && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Order
                  </label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    {optionGroup.sort_order}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="space-y-6">
          <div className="card">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Status</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-2">
                {optionGroup.active ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <XCircleIcon className="h-4 w-4 mr-1" />
                    Inactive
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {optionGroup.active 
                  ? 'This option group is available for use'
                  : 'This option group is disabled'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Option Items Section */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Option Items</h2>
            <Link
              to={`/option-items/new?groupId=${optionGroup.id}`}
              className="btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Item
            </Link>
          </div>
        </div>
        <div className="p-6">
          {optionItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Label
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price Delta
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Default
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sort Order
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {optionItems.map((item: any) => (
                    <tr 
                      key={item.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleOptionItemClick(item.id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <TagIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.label}</div>
                            <div className="text-sm text-gray-500">ID: {item.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <CurrencyEuroIcon className="h-4 w-4 text-gray-400 mr-1" />
                          <span className={`font-medium ${item.price_delta_cents > 0 ? 'text-green-600' : item.price_delta_cents < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                            {formatPrice(item.price_delta_cents)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.default_selected ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Default
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Optional
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.sort_order || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🏷️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No option items yet</h3>
              <p className="text-gray-500 mb-6">
                Create option items to give customers choices within this group.
              </p>
              <Link
                to={`/option-items/new?groupId=${optionGroup.id}`}
                className="btn-primary"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add First Item
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Connected Variants Section */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Connected Variants</h2>
            <Link
              to={`/variant-option-groups?optionGroupId=${optionGroup.id}`}
              className="btn-secondary"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              View All
            </Link>
          </div>
        </div>
        <div className="p-6">
          {connectedVariants.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Variant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {connectedVariants.map((variant: any) => (
                    <tr 
                      key={variant.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleVariantClick(variant.product_variant?.product?.id, variant.product_variant_id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <CubeIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{variant.product_variant?.name}</div>
                            <div className="text-sm text-gray-500">ID: {variant.product_variant_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {variant.product_variant?.product?.name || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        €{(variant.product_variant?.price_cents / 100).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {variant.product_variant?.active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircleIcon className="h-3 w-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🔗</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No connected variants</h3>
              <p className="text-gray-500 mb-6">
                This option group is not connected to any product variants yet.
              </p>
              <Link
                to={`/variant-option-groups/new?optionGroupId=${optionGroup.id}`}
                className="btn-primary"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Connect Variants
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OptionGroupDetailPage
